import { Array } from "@siteimprove/alfa-array";
import { Option } from "@siteimprove/alfa-option";
import { String } from "@siteimprove/alfa-string";
import { Trampoline } from "@siteimprove/alfa-trampoline";
import { Rule } from "../rule.js";
import { GroupingRule } from "./grouping.js";
/**
 * Model for CSS layers
 *
 * @remarks
 * There are actually two kinds of `@layer` rules: block and statements.
 * Both are introduced by the same keyword, and work closely together,
 * hence they are kept here for better cohesion. They are, however,
 * represented as separated Classes, with separated `type`, mimicking the two
 * JS interfaces that exist in native DOM.
 *
 * @public
 */
export var Layer;
(function (Layer) {
    /**
     * {@link https://developer.mozilla.org/en-US/docs/Web/CSS/@layer}
     * {@link https://drafts.csswg.org/css-cascade-5/#layer-empty}
     * {@link https://developer.mozilla.org/en-US/docs/Web/API/CSSLayerStatementRule}
     */
    class StatementRule extends Rule {
        static of(layers) {
            return new StatementRule(Array.from(layers));
        }
        _layers;
        constructor(layers) {
            super("layer-statement");
            this._layers = layers;
        }
        get layers() {
            return this._layers;
        }
        toJSON() {
            return {
                ...super.toJSON(),
                layers: this._layers,
            };
        }
        toString() {
            return `@layer ${this._layers.join(", ")};`;
        }
    }
    Layer.StatementRule = StatementRule;
    (function (StatementRule) {
        function fromLayerStatementRule(json) {
            return Trampoline.done(StatementRule.of(json.layers));
        }
        StatementRule.fromLayerStatementRule = fromLayerStatementRule;
        function isLayerStatementRule(value) {
            return value instanceof StatementRule;
        }
        StatementRule.isLayerStatementRule = isLayerStatementRule;
    })(StatementRule = Layer.StatementRule || (Layer.StatementRule = {}));
    /**
     * {@link https://developer.mozilla.org/en-US/docs/Web/CSS/@layer}
     * {@link https://drafts.csswg.org/css-cascade-5/#layer-block}
     * {@link https://developer.mozilla.org/en-US/docs/Web/API/CSSLayerBlockRule}
     */
    class BlockRule extends GroupingRule {
        static of(rules, layer) {
            return new BlockRule(Option.from(layer), Array.from(rules));
        }
        _layer;
        constructor(layer, rules) {
            super("layer-block", rules);
            this._layer = layer;
        }
        get layer() {
            return this._layer;
        }
        equals(value) {
            return (value instanceof BlockRule &&
                value._layer.equals(this._layer) &&
                super.equals(value));
        }
        toJSON() {
            return {
                ...super.toJSON(),
                layer: this._layer.getOr(null),
            };
        }
        toString() {
            const rules = this._rules
                .map((rule) => String.indent(rule.toString()))
                .join("\n\n");
            return `@layer ${this._layer.isSome() ? this._layer.getUnsafe() + " " : ""}{${rules === "" ? "" : `\n${rules}\n`}}`;
        }
    }
    Layer.BlockRule = BlockRule;
    (function (BlockRule) {
        function fromLayerBlockRule(json) {
            return Trampoline.traverse(json.rules, Rule.fromRule).map((rules) => BlockRule.of(rules, json.layer));
        }
        BlockRule.fromLayerBlockRule = fromLayerBlockRule;
        function isLayerBlockRule(value) {
            return value instanceof BlockRule;
        }
        BlockRule.isLayerBlockRule = isLayerBlockRule;
    })(BlockRule = Layer.BlockRule || (Layer.BlockRule = {}));
})(Layer || (Layer = {}));
//# sourceMappingURL=layer.js.map